import { clearChatOpenPayload, loadMoreMessages } from 'app-components/flux/ai-chat';
import type { ChartRequest } from 'app-components/flux/ai-chat/aiChatTypes';

import * as aiChatSelectors from 'app-components/flux/ai-chat/aiChatSelectors';

import { getLogger } from 'app-components/utils/core';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useEffect, useRef, useMemo, useCallback, type JSX } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { makeStyles } from 'tss-react/mui';
import { randomGreeting } from './utils';
import ChatFeedback from './ChatFeedback';
import ChatDefaultState from './ChatDefaultState';
import { createMarkdownComponents } from 'utils/createMarkdownComponents';

import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { profileSelectors } from 'app-components/flux/profile';
import { AIAvatar } from 'components/AIComponents/AIAvatar';
import { useChatSession } from 'components/ChatLauncher/chatSession';
import ChartPreviewDialog from 'components/Dialogs/ChartPreviewDialog';
// import { useVoiceRecorder } from 'hooks/useVoiceRecorder';
import ChatControls from './ChatControls';
import ChatHeader from './ChatHeader';
import ChatSideBar from './ChatSideBar';
import ChartPreview from './chartPreviews/ChartPreview';

const log = getLogger('SimpleChat');

const TOP_SCROLL_THRESHOLD = 80;
const BOTTOM_SCROLL_THRESHOLD = 100;

const useStyles = makeStyles<{ isDocked: boolean }>({ name: 'AI-Chat' })((theme, { isDocked }) => ({
  dialogBorder: {
    background: theme.palette.ai.background,
    borderRadius: isDocked ? 0 : 12,
    width: isDocked ? '100%' : 500,
    height: isDocked ? '100%' : '80vh',
    padding: isDocked ? 0 : 8,
    backdropFilter: 'blur(4px)',
    zIndex: theme.zIndex.modal - 1,
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: isDocked ? theme.shadows[0] : theme.shadows[2],
    ...(isDocked
      ? {
          position: 'relative',
        }
      : {
          right: theme.spacing(2),
        }),
  },
  paper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light} 100%)`,
    backdropFilter: 'blur(16px)',
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  messageList: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(14),
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    flexGrow: 1,
    overflowY: 'auto',
    borderRadius: theme.shape.borderRadius,
    userSelect: 'text',
  },
  messageBoxUser: {
    display: 'inherit',
    textAlign: 'right',
    margin: theme.spacing(1.5, 0),
  },
  messageBoxAi: {
    display: 'flex',
    textAlign: 'left',
    margin: theme.spacing(1.5, 0),
  },
  messageBubbleUser: {
    display: 'inline-block',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
    borderRadius: '12px 4px 12px 12px',
    marginLeft: theme.spacing(5),
  },
  messageBubbleAi: {
    display: 'inline-block',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 2,
  },
  chartBubble: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
  },
  chartHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  chartTitle: {
    flex: 1,
    minWidth: 0,
  },
  streamingMessageBox: {
    textAlign: 'left',
    marginBottom: theme.spacing(1),
    display: 'flex',
  },
  spinnerBubble: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: theme.spacing(6),
    minHeight: theme.spacing(4),
  },
  messageBubbleReasoning: {
    padding: theme.spacing(0, 1),
    color: theme.palette.text.tertiary,
    borderRadius: theme.shape.borderRadius * 2,
  },
  paginationRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    color: theme.palette.text.secondary,
  },
  paginationError: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  scrollToBottom: {
    position: 'absolute',
    left: '50%',
    bottom: theme.spacing(12),
    transform: 'translateX(-50%)',
    zIndex: 2,
  },
  scrollToBottomButton: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: theme.shadows[2],
  },
}));

type SimpleChatProps = {
  handleMinimize: () => void;
  handleDock: () => void;
  isDocked: boolean;
  onClose: () => void;
  dragHandleRef?: React.RefObject<HTMLDivElement | null>;
  open?: boolean;
  isMinimized?: boolean;
  greeting?: string;
};

const AICHat = ({
  handleMinimize,
  handleDock,
  isDocked,
  onClose,
  dragHandleRef,
  open: _open,
  isMinimized: _isMinimized,
  greeting: greetingProp,
}: SimpleChatProps): JSX.Element => {
  const [input, setInput] = React.useState('');
  const [showSideBarMenu, setShowSideBarMenu] = React.useState(false);
  const [paginationError, setPaginationError] = React.useState<string | null>(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = React.useState(true);
  const [activeChart, setActiveChart] = React.useState<ChartRequest | null>(null);
  const { classes, theme } = useStyles({ isDocked });

  const dispatch = useAppDispatch();
  const { firstName = '' } = useAppSelector((state) => profileSelectors.getProfile(state)) || {};
  const fallbackGreeting = useMemo(() => randomGreeting(), []);
  const greeting = greetingProp ?? fallbackGreeting;

  const { conversationId } = useChatSession();
  const isLoadingConversations = useAppSelector(aiChatSelectors.isLoadingConversations);
  const hasMore = useAppSelector((state) =>
    conversationId ? aiChatSelectors.selectConversationHasMore(state, conversationId) : false,
  );
  const isLoadingMore = useAppSelector((state) =>
    conversationId ? aiChatSelectors.selectConversationIsLoadingMore(state, conversationId) : false,
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevScrollHeightRef = useRef<number | null>(null);
  const isInitialLoadRef = useRef(true);
  const autoScrollEnabledRef = useRef(true);

  const {
    messages,
    streamingMessage,
    reasoningMessage,
    isConversationLoading,
    sendMessage,
    isReady,
    error,
    prepareConversation,
    startNewConversation,
  } = useChatSession();
  const chatOpenPayload = useAppSelector(aiChatSelectors.getChatOpenPayload);
  const [pendingAutoSendMessage, setPendingAutoSendMessage] = React.useState<string | null>(null);

  const isStreaming = useMemo(() => streamingMessage.length > 0, [streamingMessage]);
  const localDragHandleRef = useRef<HTMLDivElement | null>(null);
  const handleRef = dragHandleRef ?? localDragHandleRef;
  const isLoading = (isLoadingMore || isLoadingConversations) && !streamingMessage;
  const isPaginating = isLoadingMore && prevScrollHeightRef.current != null;
  const lastMessage = messages[messages.length - 1];
  const isWaitingForResponse = messages.length > 0 && (isConversationLoading || lastMessage?.role === 'user');
  const showStatus = (isWaitingForResponse || Boolean(reasoningMessage)) && !streamingMessage;

  // Default state: when conversation hasn't started (no messages, not streaming, not loading, not showing status)
  const isDefaultState = useMemo(
    () => messages.length === 0 && !streamingMessage && !isLoading && !showStatus && !isLoadingConversations,
    [messages.length, streamingMessage, isLoading, showStatus, isLoadingConversations],
  );
  const showScrollToBottom = !isDefaultState && !isAutoScrollEnabled;

  const markdownComponents = useMemo(() => createMarkdownComponents(theme), [theme]);

  useEffect(() => {
    if (!chatOpenPayload) return;
    const { conversationMode } = chatOpenPayload;
    const shouldStartNew = conversationMode === 'new';
    const shouldPrepareExisting = conversationMode === 'existing' || (!conversationMode && chatOpenPayload.autoSend);
    const message = chatOpenPayload.message?.trim();

    if (shouldStartNew) {
      startNewConversation().catch((err: unknown) => {
        log.error('AIChat: Failed to start new conversation for open payload', err);
      });
    } else if (shouldPrepareExisting) {
      prepareConversation().catch((err: unknown) => {
        log.error('AIChat: Failed to prepare conversation for open payload', err);
      });
    }

    if (message) {
      if (chatOpenPayload.autoSend) {
        setPendingAutoSendMessage(message);
      } else if (!input.trim()) {
        setInput(message);
      }
    }
    dispatch(clearChatOpenPayload());
  }, [chatOpenPayload, dispatch, input, prepareConversation, startNewConversation]);

  useEffect(() => {
    if (!pendingAutoSendMessage || !isReady) return;
    sendMessage(pendingAutoSendMessage).catch((err: unknown) => {
      log.error('AIChat: Failed to send open payload message', err);
    });
    setPendingAutoSendMessage(null);
  }, [pendingAutoSendMessage, isReady, sendMessage]);

  // Voice recording disabled for now
  // const { recording, startRecording, stopRecording } = useVoiceRecorder({
  //   send: (buf) => llmSocketInterface.send(buf), // WebSocket, WebRTC, MediaSource, whatever
  //   isReady: true,
  //   addLog: (m) => log.debug(m),
  //   sampleRate: 16000,
  //   bufferSize: 4096,
  // });
  const sendMessageText = useCallback(
    (text: string, trackSend: boolean = false) => {
      if (!text.trim()) return;
      if (!isReady || isLoading) return;
      sendMessage(text, { trackSend }).catch((err: unknown) => {
        log.error('AIChat: Failed to send message', err);
      });
      setInput('');
    },
    [isLoading, isReady, sendMessage],
  );

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Don’t fight the "load older messages" logic
    if (prevScrollHeightRef.current != null) return;
    if (!autoScrollEnabledRef.current) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, streamingMessage]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // First load: just jump to bottom
    if (isInitialLoadRef.current) {
      container.scrollTop = container.scrollHeight;
      isInitialLoadRef.current = false;
      return;
    }

    // After loading older messages: keep viewport anchored
    if (prevScrollHeightRef.current != null && !isLoadingMore) {
      const diff = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop += diff;
      prevScrollHeightRef.current = null;
    }
  }, [messages.length, isLoadingMore]);

  const loadOlderMessages = useCallback((): void => {
    if (!hasMore || isLoadingMore || !conversationId) return;

    const container = scrollRef.current;
    if (container) {
      // capture height *before* we prepend
      prevScrollHeightRef.current = container.scrollHeight;
    }

    setPaginationError(null);

    dispatch(loadMoreMessages(conversationId))
      .unwrap()
      .catch((err: unknown) => {
        if (typeof err === 'string' && err === 'No more items to load') {
          return;
        }
        log.error('Failed to load older messages', err);
        setPaginationError('Unable to load older messages.');
      });
  }, [dispatch, hasMore, isLoadingMore, conversationId]);

  const setAutoScrollEnabled = useCallback((enabled: boolean) => {
    if (autoScrollEnabledRef.current === enabled) return;
    autoScrollEnabledRef.current = enabled;
    setIsAutoScrollEnabled(enabled);
  }, []);

  const updateAutoScrollFromUser = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceFromBottom <= BOTTOM_SCROLL_THRESHOLD;
    setAutoScrollEnabled(isNearBottom);
  }, [setAutoScrollEnabled]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const container = scrollRef.current;
      if (!container) return;

      if (event.nativeEvent.isTrusted) {
        updateAutoScrollFromUser();
      }

      if (container.scrollTop <= TOP_SCROLL_THRESHOLD) {
        loadOlderMessages();
      }
    },
    [loadOlderMessages, updateAutoScrollFromUser],
  );

  const handleScrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    setAutoScrollEnabled(true);
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [setAutoScrollEnabled]);

  const handleOpenChartDialog = (chart: ChartRequest) => {
    setActiveChart(chart);
  };

  const handleCloseChartDialog = () => {
    setActiveChart(null);
  };

  return (
    <Box className={classes.dialogBorder}>
      <Paper id='simple-chat-interface' elevation={3} className={classes.paper}>
        <ChatHeader
          onClose={onClose}
          setShowSideBarMenu={setShowSideBarMenu}
          handleMinimize={handleMinimize}
          handleDock={handleDock}
          isDocked={isDocked}
          handleRef={handleRef}
          isDefaultState={isDefaultState}
        />
        {showSideBarMenu && <ChatSideBar open={!!showSideBarMenu} onClose={() => setShowSideBarMenu(false)} />}

        <Box ref={scrollRef} onScroll={handleScroll} className={classes.messageList}>
          {isPaginating && (
            <Box className={classes.paginationRow} role='status' aria-live='polite'>
              <CircularProgress size={16} color='inherit' />
              <Typography variant='captionRegular'>Loading older messages...</Typography>
            </Box>
          )}
          {paginationError && !isLoadingMore && (
            <Box className={classes.paginationError} role='alert'>
              <Typography variant='captionRegular' color='error'>
                {paginationError}
              </Typography>
              <Button size='small' variant='text' color='error' onClick={loadOlderMessages}>
                Retry
              </Button>
            </Box>
          )}
          {isDefaultState && (
            <ChatDefaultState
              firstName={firstName}
              greeting={greeting}
              input={input}
              isLoading={isLoading}
              isStreaming={isStreaming}
              isReady={isReady}
              setInput={setInput}
              sendMessageText={sendMessageText}
            />
          )}
          {messages

            .filter(
              (m) => (m.role === 'user' || m.role === 'assistant') && (m.type === 'message' || m.type === 'error'),
            )
            .map((msg, i) => {
              const messageKey = msg.id ?? msg.content?.callId ?? i;
              const isUser = msg.role === 'user';

              const { text } = msg.content ?? '';
              const charts: ChartRequest[] = !isUser && msg.type === 'message' ? (msg.content?.charts ?? []) : [];
              // Disabled relatedQuestions and legal disclaimer for now
              // const relatedQuestions = metadata?.relatedQuestions ?? [];
              // const customLegalDisclaimer = metadata?.customLegalDisclaimer ?? false;

              return (
                <React.Fragment key={messageKey}>
                  {!isUser &&
                    charts.map((chart, chartIndex) => (
                      <Box
                        key={`${messageKey}-chart-${chartIndex}`}
                        sx={{ marginLeft: theme.spacing(5), marginTop: 1.5 }}
                      >
                        <Box className={classes.chartBubble}>
                          <Box className={classes.chartHeader}>
                            <Typography
                              variant='headlineMedium'
                              gutterBottom
                              noWrap
                              title={chart.title || 'Chart preview'}
                              className={classes.chartTitle}
                            >
                              {chart.title || 'Chart preview'}
                            </Typography>
                            <IconButton
                              size='small'
                              aria-label='Open chart'
                              onClick={() => handleOpenChartDialog(chart)}
                            >
                              <LaunchOutlinedIcon fontSize='small' />
                            </IconButton>
                          </Box>
                          {chart.description && (
                            <Typography variant='captionRegular' color='textSecondary'>
                              {chart.description}
                            </Typography>
                          )}
                          <Box sx={{ marginTop: theme.spacing(1) }}>
                            <ChartPreview chart={chart} />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  <Box className={isUser ? classes.messageBoxUser : classes.messageBoxAi}>
                    {!isUser && (
                      <Box paddingRight={1} paddingTop={2} sx={{ zIndex: 0 }}>
                        <Box sx={{ position: 'relative', zIndex: 0 }}>
                          <AIAvatar />
                        </Box>
                      </Box>
                    )}
                    <Typography
                      component='div'
                      variant='paragraphRegular'
                      className={isUser ? classes.messageBubbleUser : classes.messageBubbleAi}
                    >
                      {msg.type === 'error' ? (
                        <Typography sx={{ color: theme.palette.error.main }}>{text}</Typography>
                      ) : (
                        <ReactMarkdown
                          remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                          rehypePlugins={[rehypeRaw]}
                          components={markdownComponents}
                        >
                          {text}
                        </ReactMarkdown>
                      )}
                    </Typography>
                  </Box>
                  {!isUser && msg.type === 'message' && (
                    <Box sx={{ marginLeft: theme.spacing(5) }}>
                      <ChatFeedback messageId={messageKey} aiResponse={text || ''} />
                    </Box>
                  )}
                  {/* <Box>
                  {customLegalDisclaimer && <Typography>Legal Disclaimer</Typography>}
                  {relatedQuestions?.map((question: string, i: number) => (
                    <Chip
                      sx={{ margin: 0.5 }}
                      variant='outlined'
                      label={question}
                      key={`${question}-${i}`}
                      onClick={() => askRelatedQuestion(question)}
                    />
                  ))}
                </Box> */}
                </React.Fragment>
              );
            })}

          {showStatus && (
            <Box className={classes.streamingMessageBox}>
              <AIAvatar />
              <Typography
                component='div'
                variant='paragraphRegular'
                className={`${classes.messageBubbleReasoning} ${classes.spinnerBubble}`}
              >
                {reasoningMessage ? (
                  <Box display='flex' alignItems='center' gap={1}>
                    {reasoningMessage}
                  </Box>
                ) : (
                  <CircularProgress size={16} color='inherit' />
                )}
              </Typography>
            </Box>
          )}

          {streamingMessage && (
            <Box className={classes.streamingMessageBox}>
              <AIAvatar />
              <Typography component='div' variant='paragraphRegular' className={classes.messageBubbleAi}>
                <ReactMarkdown
                  remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                  rehypePlugins={[rehypeRaw]}
                  components={markdownComponents}
                >
                  {streamingMessage}
                </ReactMarkdown>
              </Typography>
            </Box>
          )}

          {error && (
            <Box className={classes.messageBoxAi}>
              <Typography color='error'>{error}</Typography>
            </Box>
          )}
        </Box>
        {activeChart && <ChartPreviewDialog open onClose={handleCloseChartDialog} chart={activeChart} />}
        {!isDefaultState && (
          <>
            {showScrollToBottom && (
              <Box className={classes.scrollToBottom}>
                <IconButton
                  className={classes.scrollToBottomButton}
                  size='small'
                  aria-label='Scroll to bottom'
                  onClick={handleScrollToBottom}
                >
                  <ArrowDownwardRoundedIcon fontSize='small' />
                </IconButton>
              </Box>
            )}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: theme.spacing(12),
                background: `linear-gradient(to top, ${theme.palette.background.paper} 50%, transparent)`,
                zIndex: 0,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
            <ChatControls
              // recording={recording}
              // startRecording={startRecording}
              // stopRecording={stopRecording}
              input={input}
              isLoading={isLoading}
              isStreaming={isStreaming}
              isReady={isReady}
              setInput={setInput}
              sendMessageText={sendMessageText}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AICHat;
